import React, {
    useCallback, useMemo, useRef, useState
} from 'react'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { useDispatch } from 'react-redux'
import { chain, filter } from 'lodash'
import styled from 'styled-components'
import Icon from 'components/Icon'
import Button from 'components/Button'
import { useClickOutSide, useDeleteItem } from 'hooks'
import { _, colors } from 'utils'
import { addModal } from 'features/modals/modalSlice'
import { deleteNavigationMenu, updateNavigationSubMenu } from '../graphql/mutation'
import ContextMenu from './ContextMenu'
import NavSubMenuEditor from './NavSubMenuEditor'
import OptionButton from './OptionButton'
import NavSubMenu from './NavSubMenu'
import NavMenuEditor from './NavMenuEditor'
import {
    findMaxOrder, NavigationMenu, reorderItems, updateOrderItems
} from '.'

const SubMenuWrapper = styled.div`
    vertical-align: top;
`
const SubMenusWrapper = styled.div`
    position: relative;
    display: flex;

    ${SubMenuWrapper} + ${SubMenuWrapper} {
        margin-left: 10px;
    }
`
const CreateSectionButtonWrapper = styled.div`
    vertical-align: top;
    margin-left: 15px;
`

const S = {
    Wrapper: styled(ContextMenu.Wrapper)`
        position: relative;
        display: flex;
        align-items: center;
        margin-right: 10px;
        background: #fff;
    `,
    Label: styled(ContextMenu.Trigger)`
        background: transparent;
        color: rgba(45, 48, 53, 0.75);
        white-space: nowrap;
        margin: -5px;
        padding: 5px;
    `,
    DropDownContainer: styled.div`
        position: absolute;
        z-index: 100;
        top: 100%;
        left: 0;
        margin-top: 10px;
        background: #FFFFFF;
        box-shadow: 0px 2px 4px ${colors.shadow};
        border-radius: 2px;
        padding: 10px;
        white-space: nowrap;
    `,
    Divider: styled.div`
        width: 0;
        margin: 0 5px;
        border-left: 1px solid ${colors.iconOpacity(0.1)};
    `,
    DragDropWrapper: styled.div`
        display: inline-flex;
    `,
    DraggableWrapper: styled.div`
        display: flex;
    `,
    CreateButton: styled(Button)`
        background: transparent;
        color: ${colors.icon};
        border: 1px dashed ${colors.icon};
        margin-top: 5px;
    `,
    SubMenuWrapper,
    SubMenusWrapper,
    CreateSectionButtonWrapper,
}

type CreateButtonProps = {
    onClick: () => void
    label: string
}

export const CreateButton = ({ onClick, label }: CreateButtonProps) => (
    <S.CreateButton
        icon="plusBox"
        variant="secondary"
        iconStyle={{ marginRight: 10 }}
        iconProps={{ color: colors.icon }}
        onClick={onClick}
    >
        {label}
    </S.CreateButton>
)

type Props = {
    menu: NavigationMenu
    onDeleted: () => void
}
const NavMenu = ({
    menu: initMenu,
    onDeleted,
    ...dragHandleProps
}: Props) => {
    const dispatch = useDispatch()
    const [subMenusShowed, showSubMenus] = useState(false)
    const [menu, setMenu] = useState<NavigationMenu>(initMenu)
    const [subMenus, setSubMenus] = useState<NavigationMenu['subMenus']>(initMenu.subMenus || [])
    const dropdownRef = useRef<HTMLDivElement>()
    const { showDeleteConfirmModal } = useDeleteItem(deleteNavigationMenu, onDeleted)
    useClickOutSide(dropdownRef, () => subMenusShowed && showSubMenus(false))

    const showCreateSubMenuModal = useCallback(() => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <NavSubMenuEditor
                    close={close}
                    newOrder={findMaxOrder(subMenus) + 1}
                    menuId={`${menu.id}`}
                    onSubmitted={(createdSubMenu) => setSubMenus([...subMenus, createdSubMenu])}
                />
            ),
            shouldNotWrap: true
        }))
    }, [dispatch, menu.id, subMenus])

    const showEditNavMenuModal = useCallback((e) => {
        e.stopPropagation()
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <NavMenuEditor
                    close={close}
                    onSubmitted={
                        (updatedMenu, isReplaced) => (isReplaced ? onDeleted() : setMenu(updatedMenu))
                    }
                    navMenu={{
                        id: menu.id,
                        title: menu.text,
                        order: menu.order,
                        spaceId: -1
                    }}
                />
            ),
            shouldNotWrap: true
        }))
    }, [dispatch, menu.id, menu.order, menu.text, onDeleted])

    const handleDelete = useCallback(() => {
        showDeleteConfirmModal(menu.id)
    }, [menu.id, showDeleteConfirmModal])

    const subMenuViews = useMemo(() => chain(subMenus)
        .sortBy('order')
        .map((subMenu, index) => (
            <S.DraggableWrapper key={subMenu.id}>
                {index !== 0 && <S.Divider />}
                <Draggable draggableId={`${subMenu.id}`} index={index}>
                    {(provided) => (
                        <S.SubMenuWrapper
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                        >
                            <NavSubMenu
                                subMenu={subMenu}
                                onDeleted={() => setSubMenus(filter(subMenus, (s) => s.id !== subMenu.id))}
                                {...provided.dragHandleProps}
                            />
                        </S.SubMenuWrapper>
                    )}
                </Draggable>
            </S.DraggableWrapper>
        ))
        .value(), [subMenus])

    const handleDragEnd = useCallback(({ destination, source }) => {
        reorderItems(subMenus, source?.index, destination?.index)

        // sync new order with server
        updateOrderItems(subMenus, updateNavigationSubMenu)

        // Update list for the new orders
        setSubMenus([...subMenus])
    }, [subMenus])

    return (
        <S.Wrapper onClick={() => showSubMenus(true)}>
            <div {...dragHandleProps}>
                <Icon icon="dragVertical" style={{ marginBottom: 2 }} />
            </div>
            <S.Label>
                {menu.text}
                {' '}
                {subMenusShowed ? <Icon icon="chevronUp" /> : <Icon icon="chevronDown" />}

                <ContextMenu.Container>
                    <OptionButton icon="edit" onClick={showEditNavMenuModal} />
                    {!subMenus || subMenus.length === 0
                        ? <OptionButton icon="viewOff" />
                        : <OptionButton icon="view" active />}
                    <OptionButton icon="trash" onClick={handleDelete} />
                </ContextMenu.Container>
            </S.Label>

            {subMenusShowed && (
                <S.DropDownContainer ref={dropdownRef}>
                    <S.DragDropWrapper>
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId={`droppable-submenu-${menu.id}`} direction="horizontal">
                                {(provided) => (
                                    <S.SubMenusWrapper ref={provided.innerRef} {...provided.droppableProps}>
                                        {subMenuViews}
                                        {provided.placeholder}
                                    </S.SubMenusWrapper>
                                )}
                            </Droppable>
                        </DragDropContext>
                        {subMenus.length > 0 && <S.Divider />}
                        <S.CreateSectionButtonWrapper>
                            <CreateButton
                                onClick={showCreateSubMenuModal}
                                label={_('navigation.create_section')}
                            />
                        </S.CreateSectionButtonWrapper>
                    </S.DragDropWrapper>
                </S.DropDownContainer>
            )}
        </S.Wrapper>
    )
}

export default NavMenu
